import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { authentication } from '@perpay-web/authentication/singleton';
import {
    fetchSignupRequirements,
    fetchSignupRequirementsError,
    fetchSignupRequirementsReset,
    fetchSignupRequirementsSuccess,
} from '@perpay-web/components/actions/signupRequirements';
import { getAuthenticationTokenState } from '@perpay-web/components/selectors/partnerOnboardingSelectors';
import { useDataModule } from '@perpay-web/data-module/useDataModule';
import { useReduxDataModuleHookConnection } from '@perpay-web/data-module/useReduxDataModuleHookConnection';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

const useSignupRequirementsDataModule = () => {
    const hasInitializedRef = useRef(false);
    const [completedSignupRequirements, setCompletedSignupRequirements] =
        useState(false);
    const tokenState = useSelector(getAuthenticationTokenState);

    const dataModule = useDataModule(() =>
        authentication.fetchSignupRequirements(),
    );

    useReduxDataModuleHookConnection({
        dataModule,
        hookAction$,
        request: fetchSignupRequirements,
        error: fetchSignupRequirementsError,
        success: fetchSignupRequirementsSuccess,
        reset: fetchSignupRequirementsReset,
    });

    const { dataRequest, data } = dataModule;

    // Refetch data when user becomes authenticated
    useEffect(() => {
        if (window.STORYBOOK || completedSignupRequirements) {
            return;
        }

        if (
            (tokenState === 'authenticated' && hasInitializedRef.current) ||
            !hasInitializedRef.current
        ) {
            hasInitializedRef.current = true;
            dataRequest();
        }
    }, [tokenState, dataRequest, completedSignupRequirements]);

    useEffect(() => {
        if (data) {
            if (
                !data.requirements.primary.length &&
                !data.requirements.secondary.length
            ) {
                // No need to refresh signup requirements if there are no outstanding requirements
                setCompletedSignupRequirements(true);
            }
        }
    }, [data]);

    return dataModule;
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const hook = useSignupRequirementsDataModule();

        return <Context.Provider value={hook}>{children}</Context.Provider>;
    };

    return Provider;
};

export const [
    SignupRequirementsDataProvider,
    useSignupRequirementsDataContext,
] = createContextProvider(providerFactory);
